import { XIcon } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { Button } from './button'
import { useRegisterNestedDialog } from './nestedDialogLayering'
import { PortalContainerContext } from './portalContainer'
import { useVisualViewport } from './visualViewport'

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

/**
 * Layering, so these don't drift apart again:
 *   100  sticky app nav
 *   150  modal surfaces — Dialog and Sheet, which must cover the nav
 *   200  transient overlays — Select, DropdownMenu, Popover, Tooltip, Combobox
 *
 * Transient overlays sit *above* modals on purpose: they're always the most
 * recently opened surface, and a modal blocks page interaction, so nothing can be
 * hidden by them. A Select rendered inside a Dialog is the case that breaks if
 * this is inverted. The exception is a Dialog opened *from* an overlay, where the
 * dialog really is the newer surface — that overlay hides itself while the dialog is
 * up (see `nestedDialogLayering.ts`).
 */
function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-150 bg-black/50',
        className
      )}
      {...props}
    />
  )
}

/**
 * Renders nothing — it exists to tie these hooks to the dialog's open state.
 * `DialogContent` itself stays mounted while the dialog is closed (only the portal's
 * contents come and go), so the hooks have to live on something inside the portal.
 */
function OpenDialogEffects() {
  useRegisterNestedDialog()
  useVisualViewport()
  return null
}

/** The five dialog widths, each the most it takes; a narrower screen leaves a 5px gutter each side. */
const DIALOG_SIZES = {
  /** 448px: success and announcements, one action and no form. */
  sm: 'max-w-md',
  /** 512px: edit forms and confirms. */
  default: 'max-w-lg',
  /** 672px: forms with cards, two columns or an upload area. */
  lg: 'max-w-2xl',
  /** 1024px: viewing documents, screenshots and summaries. */
  xl: 'max-w-5xl',
  /** 1200px: rosters and signing, where the content needs the width. */
  full: 'max-w-300',
} as const

export type DialogSize = keyof typeof DIALOG_SIZES

function DialogContent({
  className,
  children,
  showCloseButton = true,
  size = 'default',
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
  size?: DialogSize
}) {
  const [content, setContent] = React.useState<HTMLDivElement | null>(null)
  const contentRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      setContent(node)
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref]
  )

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      {/* content-start: iOS 18 Safari stretches this fit-content grid to full height. A full-height dialog adds content-stretch. */}
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed inset-x-0 top-[var(--visual-viewport-top,0px)] bottom-[var(--visual-viewport-bottom,0px)] z-150 m-auto grid h-fit max-h-[calc(var(--visual-viewport-height,100%)_-_10px)] w-[calc(100%-10px)] content-start gap-4 overflow-y-auto rounded-lg border p-6 shadow-lg duration-200 outline-none pointer-coarse:bottom-[calc(var(--visual-viewport-bottom,0px)+6rem)] pointer-coarse:max-h-[calc(var(--visual-viewport-height,100%)-6rem-10px)]',
          DIALOG_SIZES[size],
          className
        )}
        {...props}
        ref={contentRef}
      >
        <OpenDialogEffects />
        <PortalContainerContext.Provider value={content}>{children}</PortalContainerContext.Provider>
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close asChild>
          <Button variant="outline">Close</Button>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <span className={cn('text-xl sm:text-2xl', className)}>
      <DialogPrimitive.Title
        data-slot="dialog-title"
        className={cn('text-foreground leading-none font-semibold', className)}
        {...props}
      />
    </span>
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-md mt-2', className)}
      {...props}
    />
  )
}

/** The first field in a form dialog, so it opens there rather than on the close button that leads its header. */
function firstField(content: HTMLElement): HTMLElement | null {
  return content.querySelector<HTMLElement>(
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="combobox"]:not([disabled])'
  )
}

interface FormDialogContentProps extends Omit<React.ComponentProps<typeof DialogPrimitive.Content>, 'title'> {
  title: React.ReactNode
  /** The pinned actions, e.g. Cancel and Save. */
  footer: React.ReactNode
  /** Disables the close button, e.g. while a save is in flight. */
  closeDisabled?: boolean
  /** Wraps the fields and the footer in this form, so a submit button in the footer submits it. */
  form?: React.ComponentProps<'form'>
  size?: DialogSize
}

/**
 * The app's form dialog: the title and its close button scroll with the fields, and the footer stays pinned
 * below. It opens on the first field, except on touch screens; an onOpenAutoFocus that prevents the default
 * opens it elsewhere.
 */
function FormDialogContent({
  title,
  footer,
  closeDisabled,
  form,
  className,
  children,
  onOpenAutoFocus,
  ...props
}: FormDialogContentProps) {
  const body = (
    <>
      {/* A transparent track: the browser's default draws a filled gutter with a line down its left. */}
      {/* flex-auto, never flex-1: iOS 18 Safari sizes this fit-content column from a 0% basis and draws it 0px tall. */}
      <div
        data-slot="form-dialog-body"
        className="flex min-h-0 flex-auto flex-col gap-4 overflow-y-auto px-6 py-5 [scrollbar-color:var(--color-ink-300)_transparent] pointer-coarse:flex-none pointer-coarse:overflow-visible"
      >
        <DialogHeader className="flex-row items-start justify-between gap-3 pb-2 text-left">
          <DialogTitle>{title}</DialogTitle>
          <DialogClose asChild>
            <Button
              type="button"
              variant="iconOnly"
              size="icon-sm"
              aria-label="Close"
              className="-mr-2 shrink-0"
              disabled={closeDisabled}
            >
              <XIcon className="size-4" />
            </Button>
          </DialogClose>
        </DialogHeader>
        {children}
      </div>
      <DialogFooter className="mt-0 flex-row justify-end border-t px-6 py-4">{footer}</DialogFooter>
    </>
  )

  return (
    <DialogContent
      showCloseButton={false}
      aria-describedby={undefined}
      className={cn(
        'flex max-h-[calc(var(--visual-viewport-height,100vh)_*_0.9)] flex-col gap-0 overflow-hidden p-0 pointer-coarse:overflow-y-auto',
        className
      )}
      onOpenAutoFocus={(event) => {
        onOpenAutoFocus?.(event)
        if (event.defaultPrevented) return
        const content = event.currentTarget as HTMLElement
        // On a touch screen a focused field opens the keyboard over half the form before it has been seen.
        if (window.matchMedia('(pointer: coarse)').matches) {
          event.preventDefault()
          content.focus()
          return
        }
        const field = firstField(content)
        if (!field) return
        event.preventDefault()
        field.focus()
      }}
      {...props}
    >
      {form ? (
        <form
          {...form}
          className={cn('flex min-h-0 flex-auto flex-col pointer-coarse:flex-none', form.className)}
        >
          {body}
        </form>
      ) : (
        body
      )}
    </DialogContent>
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  FormDialogContent,
}
